import React, {useContext, useRef, useState} from "react";
import { Button, Input, Spin } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { LuSend } from "react-icons/lu";
import ChatContext from "../../../../context/chat/context";
import { IChatMessageRequest } from "../../../../models/chat/request";
import {IMedium} from "../../../../models/medium/response";
import FileUploader from "../../../../components/form-components/file-uploader";
import {MediumFor} from "../../../../models/medium/enum";

const { TextArea } = Input;

interface Props {
    setScrollToBottomOnNewMessage: (val: boolean) => void;
}

const ChatInputArea: React.FC<Props> = ({
                                            setScrollToBottomOnNewMessage,
                                        }) => {
    const { loading, selectedChatId, actions: { sendMessage } } = useContext(ChatContext);

    const [message, setMessage] = useState<string>("");
    const [file, setFile] = useState<File | null>(null);
    const [filePreview, setFilePreview] = useState<string | null>(null);

    // ✅ إضافة state للـ uploaded file data
    const [uploadedFile, setUploadedFile] = useState<IMedium | null>(null);
    
    // ✅ إضافة state للـ upload loading
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const handleEmojiSelect = (emoji: any) => {
        setMessage((prev: string) => prev + emoji.native);
        setShowEmojiPicker(false);
    };

    // ❌ إزالة هاد الـ function لأننا بنستخدم FileUploader
    // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     if (e.target.files && e.target.files[0]) {
    //         const selectedFile = e.target.files[0];
    //         setFile(selectedFile);
    //         if (selectedFile.type.startsWith("image/")) {
    //             setFilePreview(URL.createObjectURL(selectedFile));
    //         } else {
    //             setFilePreview(selectedFile.name);
    //         }
    //     }
    // };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePreview(null);
        setUploadedFile(null); // ✅ إضافة
        setIsUploading(false); // ✅ إضافة
    };

    const handleSendMessage = async () => {
        if (!message.trim() && !uploadedFile) return; // ✅ تغيير الشرط

        // ✅ التحقق من أن uploadedFile يحتوي على URL صحيح
        if (uploadedFile && !uploadedFile.url) {
            console.error("❌ uploadedFile missing URL:", uploadedFile);
            return;
        }

        const request: IChatMessageRequest = {
            body: message,
            chatId: selectedChatId!,
            file: null, // لا نرسل الملف، فقط ID
            fileId: uploadedFile?.id || null, // ✅ إرسال ID الملف المرفوع
            isAdmin: 1,
            // إضافة معلومات الصورة للعرض المحلي
            _localFile: uploadedFile ? {
                url: uploadedFile.url,
                extension: uploadedFile.extension || 'jpg' // افتراضي إذا لم يكن موجود
            } : null
        };

        console.log("📤 Request being sent:", request);
        console.log("📤 _localFile:", request._localFile);
        console.log("📤 uploadedFile:", uploadedFile);

        try {
            setMessage("");
            setFile(null);
            setFilePreview(null);
            setUploadedFile(null); // ✅ إضافة
            setIsUploading(false); // ✅ إضافة
            setScrollToBottomOnNewMessage(true);
            sendMessage(request);
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const fileUploaderRef = useRef<any>(null);

    // ✅ تحديث الـ function
    const handleFileUpload = (response: IMedium | null) => {
        console.log("🔄 handleFileUpload called with:", response);
        
        if (response === null) {
            // ✅ هذا يعني أن الرفع بدأ
            setIsUploading(true);
            console.log("🚀 Upload started...");
            return;
        }
        
        if (response) {
            console.log("✅ File uploaded successfully:", response);
            console.log("✅ File URL:", response.url);
            console.log("✅ File extension:", response.extension);

            // حفظ بيانات الملف المرفوع
            setUploadedFile(response);
            setFilePreview(response.url);
            setIsUploading(false); // ✅ إيقاف الـ loading

        } else {
            console.log("❌ File upload cancelled or failed");
            setUploadedFile(null);
            setFilePreview(null);
            setIsUploading(false); // ✅ إيقاف الـ loading
        }
    };

    // ✅ إضافة function للـ upload start
    // const handleUploadStart = () => {
    //     setIsUploading(true);
    //     console.log("🚀 Upload started...");
    // };

    // ✅ إضافة function للـ before upload
    // const handleBeforeUpload = (file: File) => {
    //     setIsUploading(true);
    //     console.log("🚀 Upload started...");
    //     return file;
    // };

    const triggerFileUpload = () => {
        // تفعيل الـ upload programmatically
        const uploadInput = fileUploaderRef.current?.querySelector('input[type="file"]');
        if (uploadInput) {
            uploadInput.click();
        }
    };

    return (
        <div style={{ padding: "10px", borderTop: "1px solid #2b363a" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <TextArea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    placeholder="Type a message..."
                    style={{ flex: 1, borderRadius: "20px", padding: "10px" }}
                />

                <Button
                    type="primary"
                    shape="circle"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                    😀
                </Button>

                {showEmojiPicker && (
                    <div style={{ position: "absolute", bottom: "80px", right: "20px", zIndex: 1000 }}>
                        <Picker data={data} onEmojiSelect={handleEmojiSelect} />
                    </div>
                )}

                {/* ✅ إزالة الـ input المخفي لأننا بنستخدم FileUploader */}
                {/* <input
                    type="file"
                    id="fileInput"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                /> */}

                <div>
                    {/* الـ button المرئي مع loading state */}
                    <Button
                        type="primary"
                        shape="circle"
                        onClick={triggerFileUpload}
                        disabled={isUploading}
                        icon={isUploading ? <LoadingOutlined spin /> : "📎"}
                    >
                        {isUploading ? "" : ""}
                    </Button>

                    {/* الـ FileUploader مخفي */}
                    <div ref={fileUploaderRef} style={{ display: 'none' }}>
                        <FileUploader
                            fileFor={MediumFor.UserProfile}
                            onChange={handleFileUpload}
                            type="DRAGGER"
                            showUploadList={false}
                        />
                    </div>
                </div>

                {/* ❌ إزالة الـ button الثاني المكرر */}
                {/* <Button
                    type="primary"
                    shape="circle"
                    onClick={() => document.getElementById("fileInput")?.click()}
                >
                    📎
                </Button> */}

                {/* ✅ تحديث الـ preview ليعرض الملف المرفوع مع loading */}
                {filePreview && (
                    <div style={{ marginLeft: "10px", position: "relative" }}>
                        {filePreview.startsWith("blob:") || filePreview.startsWith("http") ? (
                            <div style={{ position: "relative" }}>
                                <img
                                    src={filePreview}
                                    alt="Preview"
                                    style={{ 
                                        width: "40px", 
                                        height: "40px", 
                                        borderRadius: "5px",
                                        opacity: isUploading ? 0.6 : 1,
                                        filter: isUploading ? "grayscale(50%)" : "none"
                                    }}
                                />
                                {/* ✅ إضافة loading overlay */}
                                {isUploading && (
                                    <div style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        zIndex: 2
                                    }}>
                                        <Spin 
                                            indicator={<LoadingOutlined style={{ color: "#1890ff", fontSize: "16px" }} spin />} 
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span>{filePreview}</span>
                        )}

                        <Button
                            type="link"
                            icon={<CloseOutlined />}
                            style={{
                                position: "absolute",
                                top: "-5px",
                                right: "-5px",
                                padding: 0,
                                minWidth: "auto",
                            }}
                            onClick={handleRemoveFile}
                        />

                        {/* ✅ تحديث عرض حالة الرفع */}
                        {isUploading ? (
                            <div style={{
                                position: "absolute",
                                bottom: "-15px",
                                left: "0",
                                fontSize: "10px",
                                color: "#1890ff",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}>
                                <LoadingOutlined style={{ fontSize: "10px" }} spin />
                                Uploading...
                            </div>
                        ) : uploadedFile ? (
                            <div style={{
                                position: "absolute",
                                bottom: "-15px",
                                left: "0",
                                fontSize: "10px",
                                color: "green"
                            }}>
                                ✅ Uploaded
                            </div>
                        ) : null}
                    </div>
                )}

                <Button
                    type="primary"
                    shape="circle"
                    onClick={handleSendMessage}
                    disabled={loading.includes('sendMessage') || isUploading}
                >
                    <LuSend />
                </Button>
            </div>
        </div>
    );
};

export default ChatInputArea;